import type { ChartData, PlanetPosition, House, Aspect, ZodiacSign } from '../types'

// 星座列表
const zodiacSigns: ZodiacSign[] = [
  'Aries', 'Taurus', 'Gemini', 'Cancer',
  'Leo', 'Virgo', 'Libra', 'Scorpio',
  'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
]

// 星座起始黄经（度）
// const zodiacStartDegrees: Record<ZodiacSign, number> = {
//   Aries: 0, Taurus: 30, Gemini: 60, Cancer: 90,
//   Leo: 120, Virgo: 150, Libra: 180, Scorpio: 210,
//   Sagittarius: 240, Capricorn: 270, Aquarius: 300, Pisces: 330
// }

// 获取星座
function getZodiacSign(degree: number): ZodiacSign {
  const normalizedDegree = degree % 360
  const signIndex = Math.floor(normalizedDegree / 30)
  return zodiacSigns[signIndex]
}

// 简化版星盘计算（使用天文算法近似）
interface CalculateParams {
  birthDate: string
  birthTime: string
  birthLocation: string
}

export async function calculateChart(params: CalculateParams): Promise<ChartData> {
  const { birthDate, birthTime, birthLocation } = params

  // 解析出生时间
  const [year, month, day] = birthDate.split('-').map(Number)
  const [hour, minute] = birthTime.split(':').map(Number)

  // 计算儒略日（简化版）
  const jd = calculateJulianDay(year, month, day, hour, minute)

  // 计算行星位置（简化版，使用近似算法）
  const planets: Record<string, PlanetPosition> = {
    sun: calculateSunPosition(jd),
    moon: calculateMoonPosition(jd),
    mercury: calculateMercuryPosition(jd),
    venus: calculateVenusPosition(jd),
    mars: calculateMarsPosition(jd),
    jupiter: calculateJupiterPosition(jd),
    saturn: calculateSaturnPosition(jd),
    uranus: calculateUranusPosition(jd),
    neptune: calculateNeptunePosition(jd),
    pluto: calculatePlutoPosition(jd),
  }

  // 计算上升星座（简化版）
  const ascendantDegree = calculateAscendant(jd, birthLocation)
  const ascendant = {
    longitude: ascendantDegree,
    sign: getZodiacSign(ascendantDegree),
    degree: ascendantDegree % 30,
  }

  // 计算天顶（简化版）
  const midheavenDegree = (ascendantDegree + 270) % 360
  const midheaven = {
    longitude: midheavenDegree,
    sign: getZodiacSign(midheavenDegree),
    degree: midheavenDegree % 30,
  }

  // 计算宫位（Placidus 系统简化版）
  const houses: House[] = calculateHouses(ascendantDegree)

  // 计算相位
  const aspects = calculateAspects(planets)

  return {
    planets,
    houses,
    aspects,
    ascendant,
    midheaven,
  }
}

// 计算儒略日
function calculateJulianDay(year: number, month: number, day: number, hour: number, minute: number): number {
  if (month <= 2) {
    year -= 1
    month += 12
  }
  const a = Math.floor(year / 100)
  const b = 2 - a + Math.floor(a / 4)
  const dayFraction = (hour + minute / 60) / 24
  return Math.floor(365.25 * (year + 4716)) + Math.floor(30.6001 * (month + 1)) + day + b - 1524.5 + dayFraction
}

// 计算太阳位置
function calculateSunPosition(jd: number): PlanetPosition {
  // 简化算法：太阳平均黄经
  const T = (jd - 2451545.0) / 36525 // 从J2000起算的儒略世纪数
  const L0 = 280.46646 + 36000.76983 * T + 0.0003032 * T * T
  const longitude = (L0 % 360 + 360) % 360

  return {
    longitude,
    sign: getZodiacSign(longitude),
    degree: longitude % 30,
    house: 1, // 临时，需要根据上升星座计算
    retrograde: false,
  }
}

// 计算月亮位置
function calculateMoonPosition(jd: number): PlanetPosition {
  // 简化算法
  const T = (jd - 2451545.0) / 36525
  const L = 218.3164477 + 481267.88123421 * T - 0.0015786 * T * T
  const longitude = (L % 360 + 360) % 360

  return {
    longitude,
    sign: getZodiacSign(longitude),
    degree: longitude % 30,
    house: 1,
    retrograde: false,
  }
}

// 其他行星位置（简化版，基于平均运动）
function calculateMercuryPosition(jd: number): PlanetPosition {
  const T = (jd - 2451545.0) / 36525
  const L = 252.250906 + 149472.6746358 * T
  const longitude = (L % 360 + 360) % 360
  return { longitude, sign: getZodiacSign(longitude), degree: longitude % 30, house: 1, retrograde: Math.random() < 0.1 }
}

function calculateVenusPosition(jd: number): PlanetPosition {
  const T = (jd - 2451545.0) / 36525
  const L = 181.979801 + 58517.8156760 * T
  const longitude = (L % 360 + 360) % 360
  return { longitude, sign: getZodiacSign(longitude), degree: longitude % 30, house: 1, retrograde: Math.random() < 0.1 }
}

function calculateMarsPosition(jd: number): PlanetPosition {
  const T = (jd - 2451545.0) / 36525
  const L = 355.433000 + 19140.2993039 * T
  const longitude = (L % 360 + 360) % 360
  return { longitude, sign: getZodiacSign(longitude), degree: longitude % 30, house: 1, retrograde: Math.random() < 0.05 }
}

function calculateJupiterPosition(jd: number): PlanetPosition {
  const T = (jd - 2451545.0) / 36525
  const L = 34.351519 + 3034.9056606 * T
  const longitude = (L % 360 + 360) % 360
  return { longitude, sign: getZodiacSign(longitude), degree: longitude % 30, house: 1, retrograde: false }
}

function calculateSaturnPosition(jd: number): PlanetPosition {
  const T = (jd - 2451545.0) / 36525
  const L = 50.077444 + 1222.1138488 * T
  const longitude = (L % 360 + 360) % 360
  return { longitude, sign: getZodiacSign(longitude), degree: longitude % 30, house: 1, retrograde: false }
}

function calculateUranusPosition(jd: number): PlanetPosition {
  const T = (jd - 2451545.0) / 36525
  const L = 314.055005 + 428.4669983 * T
  const longitude = (L % 360 + 360) % 360
  return { longitude, sign: getZodiacSign(longitude), degree: longitude % 30, house: 1, retrograde: false }
}

function calculateNeptunePosition(jd: number): PlanetPosition {
  const T = (jd - 2451545.0) / 36525
  const L = 304.348665 + 218.4862002 * T
  const longitude = (L % 360 + 360) % 360
  return { longitude, sign: getZodiacSign(longitude), degree: longitude % 30, house: 1, retrograde: false }
}

function calculatePlutoPosition(jd: number): PlanetPosition {
  const T = (jd - 2451545.0) / 36525
  const L = 238.92881 + 145.207805 * T
  const longitude = (L % 360 + 360) % 360
  return { longitude, sign: getZodiacSign(longitude), degree: longitude % 30, house: 1, retrograde: false }
}

// 计算上升星座（简化版）
function calculateAscendant(jd: number, _location: string): number {
  // 简化算法：基于出生时间和日期的近似计算
  // const T = (jd - 2451545.0) / 36525
  // 这里使用简化的近似值，实际应该使用天文算法
  const lst = (100.46 + 0.985647352 * (jd - 2451545.0) + 15 * ((jd % 1) * 24)) % 360
  return (lst + 180) % 360
}

// 计算宫位
function calculateHouses(ascendantDegree: number): House[] {
  const houses: House[] = []
  // Placidus 系统简化版：每个宫位 30 度
  for (let i = 1; i <= 12; i++) {
    const degree = (ascendantDegree + (i - 1) * 30) % 360
    houses.push({
      number: i,
      sign: getZodiacSign(degree),
      degree: degree % 30,
    })
  }
  return houses
}

// 计算相位
function calculateAspects(planets: Record<string, PlanetPosition>): Aspect[] {
  const aspects: Aspect[] = []
  const planetNames = Object.keys(planets)

  const aspectOrbs: Record<string, number> = {
    conjunction: 8,  // 合相 0°
    sextile: 6,      // 六分相 60°
    square: 8,       // 四分相 90°
    trine: 8,        // 三分相 120°
    opposition: 8,   // 对分相 180°
  }

  for (let i = 0; i < planetNames.length; i++) {
    for (let j = i + 1; j < planetNames.length; j++) {
      const p1 = planets[planetNames[i]]
      const p2 = planets[planetNames[j]]
      const angle = Math.abs(p1.longitude - p2.longitude)
      const shortestAngle = Math.min(angle, 360 - angle)

      // 检测相位
      let aspect: string | null = null
      let orb = 0

      if (shortestAngle <= aspectOrbs.conjunction) {
        aspect = 'conjunction'
        orb = shortestAngle
      } else if (Math.abs(shortestAngle - 60) <= aspectOrbs.sextile) {
        aspect = 'sextile'
        orb = Math.abs(shortestAngle - 60)
      } else if (Math.abs(shortestAngle - 90) <= aspectOrbs.square) {
        aspect = 'square'
        orb = Math.abs(shortestAngle - 90)
      } else if (Math.abs(shortestAngle - 120) <= aspectOrbs.trine) {
        aspect = 'trine'
        orb = Math.abs(shortestAngle - 120)
      } else if (Math.abs(shortestAngle - 180) <= aspectOrbs.opposition) {
        aspect = 'opposition'
        orb = Math.abs(shortestAngle - 180)
      }

      if (aspect) {
        aspects.push({
          planet1: planetNames[i],
          planet2: planetNames[j],
          aspect: aspect as Aspect['aspect'],
          orb: Math.round(orb * 10) / 10,
        })
      }
    }
  }

  return aspects
}

// 获取赛博风格解读
export function getCyberInterpretation(planet: string, sign: string, _house: number): string {
  const interpretations: Record<string, Record<string, string>> = {
    sun: {
      Aries: '你的核心处理器运行在白羊座协议上，主频极高但容易过热。你行动迅速，决策果断，是天然的开拓者。',
      Taurus: '你的核心处理器采用金牛座架构，稳定性极佳。你追求长期价值，不喜欢频繁变动。',
      Gemini: '你的核心处理器是多核的双子座架构，并行处理能力极强。你好奇心旺盛，喜欢多线程任务。',
      Cancer: '你的核心处理器有巨蟹座的安全壳，对内存（记忆）管理严格。你情感丰富，直觉敏锐。',
      Leo: '你的核心处理器是高性能的狮子座芯片，自带聚光灯。你自信大方，天生具有领导力。',
      Virgo: '你的核心处理器采用处女座精简指令集，效率极高。你追求完美，擅长系统优化。',
      Libra: '你的核心处理器运行天秤座平衡算法，擅长协调各方资源。你追求和谐，审美能力出众。',
      Scorpio: '你的核心处理器是天蝎座的深度计算单元，擅长处理复杂加密信息。你洞察力强，意志坚定。',
      Sagittarius: '你的核心处理器采用射手座扩展架构，支持远程数据传输。你热爱自由，喜欢探索未知。',
      Capricorn: '你的核心处理器是摩羯座的工业级芯片，耐用可靠。你务实进取，有强烈的成就欲。',
      Aquarius: '你的核心处理器运行水瓶座创新算法，突破传统架构。你独立前卫，关注集体未来。',
      Pisces: '你的核心处理器采用双鱼座模糊逻辑，支持多维度感知。你富有同情心，想象力丰富。',
    },
    moon: {
      Aries: '你的缓存区采用白羊座高速缓存，情绪响应极快。你需要即时满足，情绪波动较大。',
      Taurus: '你的缓存区是金牛座的稳定存储，情绪持久且深沉。你需要安全感，喜欢舒适的环境。',
      Gemini: '你的缓存区支持双子座多通道输入，情绪变化快。你需要智力刺激，善于表达感受。',
      Cancer: '你的缓存区是巨蟹座的本地存储，情绪化且记忆深刻。你对家庭和归属感有强烈需求。',
      // ... 其他星座
    },
  }

  return interpretations[planet]?.[sign] || `${planet} 在 ${sign} 的运行状态良好，请查看详细解读。`
}
